import { Controller } from "@hotwired/stimulus";
import { createRoot } from "react-dom/client";
import Modal from "bootstrap/js/dist/modal";
import AssociationSelects from "./AssociationSelects";

const NOTHING_SELECTED = { regionalAssociation: null, localAssociation: null };

/**
 * Das "Mitglieder werben Mitglieder"-Bestellformular.
 *
 * Als Stimulus-Controller verbindet es sich sowohl beim vollen Seitenaufruf als auch
 * bei Turbo-Navigation - die frühere Inline-Initialisierung samt DOMContentLoaded-
 * Sonderbehandlung (#4796) entfällt damit.
 */
export default class extends Controller {
  static targets = [
    "form",
    "associationSelects",
    "modal",
    "preview",
    "successAlert",
    "errorAlert",
  ];

  static values = { associations: Object };

  connect() {
    this.selection = NOTHING_SELECTED;

    this.root = createRoot(this.associationSelectsTarget);
    this.root.render(
      <AssociationSelects
        associations={this.associationsValue}
        onChange={(selection) => {
          this.selection = selection;
        }}
      />,
    );

    this.modal = new Modal(this.modalTarget);
    this.hideAlerts();
  }

  disconnect() {
    this.root.unmount();
    this.modal.dispose();
  }

  /** Statt direkt abzuschicken erst die Bestellvorschau zeigen */
  showPreview(event) {
    event.preventDefault();

    this.hideAlerts();
    this.renderPreview();
    this.modal.show();
  }

  async submitOrder(event) {
    const form = this.formTarget;
    // Gegen Doppel-Klicks: waehrend des Requests und bis zum Reload nach
    // Erfolg bleibt der Button gesperrt; nur im Fehlerfall wieder freigeben.
    const button = event.currentTarget;
    button.disabled = true;

    try {
      const response = await fetch(form.action, {
        method: form.method,
        body: new FormData(form),
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });

      if (!response.ok)
        throw new Error(`Unerwartete Antwort: ${response.status}`);

      this.errorAlertTarget.hidden = true;
      this.successAlertTarget.hidden = false;
      setTimeout(() => window.location.reload(), 2000);
    } catch {
      this.successAlertTarget.hidden = true;
      this.errorAlertTarget.hidden = false;
      button.disabled = false;
    }
  }

  hideAlerts() {
    this.successAlertTarget.hidden = true;
    this.errorAlertTarget.hidden = true;
  }

  renderPreview() {
    const data = new FormData(this.formTarget);
    const value = (name) => String(data.get(`hgmp_order[${name}]`) || "");
    const giftSelect = this.formTarget.elements["hgmp_order[gift_id]"];

    this.previewTarget.replaceChildren(
      previewRow(
        "Landesverband",
        this.selection.regionalAssociation,
        "Kein Landesverband ausgewählt.",
      ),
      previewRow(
        "Ortsverein",
        this.selection.localAssociation,
        "Kein Ortsverein ausgewählt.",
      ),
      previewRow("Vorname", value("first_name")),
      previewRow("Nachname", value("last_name")),
      previewRow(
        "Straße und Hausnummer",
        `${value("street_name")} ${value("house_number")}`,
      ),
      previewRow("Postleitzahl", value("zip_code")),
      previewRow("Stadt", value("city")),
      previewRow(
        "Prämie",
        giftSelect?.value ? giftSelect.selectedOptions[0].text : null,
        "Keine Prämie ausgewählt.",
      ),
    );
  }
}

function previewRow(label, value, missingText = "") {
  const row = document.createElement("p");
  const name = document.createElement("b");
  name.textContent = `${label}: `;
  row.append(name);

  if (value && value.trim()) {
    row.append(value);
  } else {
    const missing = document.createElement("span");
    missing.className = "text-danger";
    missing.textContent = missingText;
    row.append(missing);
  }

  return row;
}
